/**
 * The price of the выезд, and the payload that creates it.
 *
 * **One quote for the whole trip, not one per car.** Delivery is charged once however many cars
 * are being filled, and the urgency surcharge is a percentage of the whole. Summing per-car
 * quotes would bill delivery twice and compute the surcharge off the wrong base — the backend
 * takes `cars[]` precisely so the client never has to do that arithmetic.
 *
 * The client does no arithmetic on money at all. Every figure rendered on the summary comes from
 * `OrderQuoteResponse`; the only number this file computes is the litres per car, and that is the
 * same value that goes on the wire.
 */
import { apiRequest } from '@/shared/api/client';
import { deliveryWindowSummary } from './model/deliveryWindow';
import type { OrderDraft } from './model/draft';
import { TIME_MODE_WIRE } from './model/draft';
import { estimatedFuelLiters } from './model/volume';

export interface QuoteCarLine {
  car_id: string;
  estimated_fuel_liters: string;
  hold_amount: string;
}

/** Every field is a decimal string. It stays a string — the client formats, never calculates. */
export interface OrderQuote {
  billed_liters: string;
  cancellation_fee_amount: string;
  cancellation_fee_text: string;
  cars: QuoteCarLine[];
  delivery_price: string;
  estimated_fuel_liters: string;
  fuel_price: string;
  fuel_unit_price: string;
  max_order_liters: string;
  min_order_liters: string;
  total_price: string;
  urgency_price: string;
  /* Сколько сняла персональная скидка со всего заказа. Все суммы выше УЖЕ со скидкой, поэтому
     вычитать это число из них ещё раз нельзя — оно идёт подписью под «Итого», вместе с суммой до
     скидки. `?` потому что бэкенд постарше полей не пришлёт. */
  discount_amount?: string;
  total_price_before_discount?: string;
}

interface CarInput {
  car_id: string;
  estimated_fuel_liters: string;
}

/**
 * Every car of the выезд, primary first, in the shape both `/quote` and `/orders` take.
 *
 * The primary car's litres come from `estimatedFuelLiters`, which is the single definition shared
 * with the volume checks — a total policed against one number and priced off another would let
 * through orders the backend then refuses.
 */
export function tripCars(draft: OrderDraft): CarInput[] {
  if (draft.carId === null) return [];
  return [
    { car_id: draft.carId, estimated_fuel_liters: estimatedFuelLiters(draft) },
    ...draft.extraCars.map((car) => ({
      car_id: car.carId,
      estimated_fuel_liters: car.liters.trim().replace(',', '.'),
    })),
  ];
}

export function fetchTripQuote(draft: OrderDraft, signal?: AbortSignal): Promise<OrderQuote> {
  return apiRequest<OrderQuote>('/v1/consumer/orders/quote', {
    method: 'POST',
    body: {
      latitude: draft.latitude,
      longitude: draft.longitude,
      delivery_speed: TIME_MODE_WIRE[draft.timeMode],
      cars: tripCars(draft),
    },
    ...(signal ? { signal } : {}),
  });
}

export interface CreatedOrder {
  id: string;
  /**
   * What the bank actually froze.
   *
   * Read rather than reused from the quote: the quote is a preview and the hold is the event. They
   * agree today, and the one number worth putting in front of somebody right after their card was
   * charged is the one the charge actually used.
   */
  held_amount: string;
}

/**
 * Заказ, как его описывает черновик, — без способа оплаты.
 *
 * Один билдер на два пути. Картой заказ создаёт клиент (`createOrder`, плюс `payment_method_id`);
 * по СБП это же описание уезжает вместе с запросом кода (`startSbpPayment`, поле `order`), и
 * заказ по нему оформляет уже сервер, когда банк подтвердит оплату. Два разных набора полей для
 * одного и того же заказа разошлись бы при первой же правке — а расхождение здесь означает
 * заказ по СБП без комментария или без окна доставки.
 *
 * `external_id` is the draft's, unchanged: the card request is retried verbatim after a network
 * failure or a closed tab, and a fresh id on retry is how one order becomes two holds on one
 * card.
 *
 * `owner_present` is what the backend actually stores; the draft's `presence` is the UI's word for
 * the same thing. `operator_comment` is mandatory in the UI for «без меня» but is sent whenever
 * it exists — somebody who *is* present may still have something to say about where to park.
 */
export function orderRequestBody(draft: OrderDraft): Record<string, unknown> {
  const scheduled = draft.timeMode === 'scheduled' ? draft.selectedWindow : null;

  return {
    external_id: draft.externalId,
    car_id: draft.carId,
    cars: tripCars(draft),
    estimated_fuel_liters: estimatedFuelLiters(draft),
    latitude: draft.latitude,
    longitude: draft.longitude,
    address_text: draft.addressLabel,
    owner_present: draft.presence === 'withMe',
    operator_comment: draft.comment.trim(),
    delivery_speed: TIME_MODE_WIRE[draft.timeMode],
    ...(scheduled
      ? {
          scheduled_from: scheduled.from,
          scheduled_to: scheduled.to,
          // «Завтра, 09:00–13:00». The instants are what the system schedules on; this is what a
          // person reads on the order and what the driver sees. Composing it server-side would
          // mean naming days in a locale the server does not have.
          desired_slot_label: deliveryWindowSummary(scheduled),
        }
      : {}),
  };
}

/**
 * Creates the order and places the hold on the chosen card.
 *
 * Только карта. Заказ по СБП клиент не создаёт вовсе: его оформляет сервер по описанию из
 * `startSbpPayment`, и делает это даже тогда, когда вкладка давно закрыта, — см. `./sbp.ts`.
 */
export interface CreateOrderOptions {
  /**
   * Чем кончился сбор данных браузера, если прошлый ответ его потребовал (3-D Secure 2.0).
   *
   * Отдельным аргументом, а не полем черновика: черновик — это то, что человек набрал, и он
   * переживает перезагрузку страницы. Признак же живёт ровно один повтор запроса и в хранилище
   * ему делать нечего.
   */
  deviceCheckDone?: boolean;
  /**
   * Короткий след сбора данных — уезжает в лог сервера и больше никуда.
   *
   * 12.09.2026 разбор отказов оплаты шёл целый день вслепую: по логам было видно, ЧТО ответил
   * банк, и не видно, что на самом деле произошло в браузере. Строчка вида
   * `server:done/loads=2/ms=340 issuer:silent/loads=0/ms=10000` отвечает на это сразу.
   */
  deviceCheckTrace?: string;
  /**
   * Человек прочитал «похоже, включён VPN» и настоял на карте.
   *
   * Без этого сервер останавливает оплату картой с иностранного адреса ДО банка (402
   * `vpn_suspected`): эмитент с такого адреса карту не проверяет, а каждая попытка сжигает номер
   * заказа у банка. С ним — отдаёт банку, и дальше слово за банком.
   */
  vpnAcknowledged?: boolean;
}

export function createOrder(
  draft: OrderDraft,
  { deviceCheckDone, deviceCheckTrace, vpnAcknowledged }: CreateOrderOptions = {},
): Promise<CreatedOrder> {
  return apiRequest<CreatedOrder>('/v1/consumer/orders', {
    method: 'POST',
    body: {
      ...orderRequestBody(draft),
      payment_method_id: draft.paymentMethodId,
      ...(deviceCheckDone === undefined ? {} : { three_ds_device_check: deviceCheckDone }),
      ...(deviceCheckTrace === undefined ? {} : { three_ds_device_check_trace: deviceCheckTrace }),
      ...(vpnAcknowledged === true ? { vpn_acknowledged: true } : {}),
    },
  });
}

/**
 * The refusals that mean something specific on the summary step.
 *
 * Everything else falls through to the generic message. These five are called out because each
 * one has a different way out, and «что-то пошло не так» hides all of them.
 */
export const SUBMIT_ERROR_TEXT: Record<string, string> = {
  hold_declined: 'Банк отклонил оплату. Попробуйте другую карту.',
  hold_network_error: 'Не удалось связаться с банком. Попробуйте ещё раз — заказ не задвоится.',
  /* Банк не принимает платежи ОТ НАС — учётные данные или права мерчанта. Единственный отказ, где
     «попробуйте ещё раз» было бы враньём: пока доступ не вернут, ответ не изменится. */
  payment_gateway_access:
    'Оплата сейчас не проходит — это на нашей стороне, мы уже разбираемся. Повторять не нужно, попробуйте позже.',
  active_order_exists_for_car: 'По этой технике уже есть активный заказ.',
  service_area_unavailable: 'Этот адрес сейчас не обслуживается.',
  account_suspended: 'Аккаунт заблокирован. Напишите в поддержку.',
};
